/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div>
    <form
      aria-labelledby="uploadheader"
      @submit.prevent="upload"
    >
      <h2
        id="uploadheader"
      >
        Upload a File
      </h2>
      <assignment-select
        v-if="showAssignmentSelect"
        @assignmentChosen="assignmentChosen"
      />
      <div class="form-group">
        <label
          for="uploadTitle"
          class="field-label"
        >
          TITLE
        </label>
        <input
          id="uploadTitle"
          v-model.trim="formValues.title"
          type="text"
          class="form-control"
          placeholder="title"
          name="title"
          maxlength="50"
          required
        >
      </div>
      <div
        v-if="speakerGroups.length > 1 && !assignmentsEnabled"
        class="form-group"
      >
        <label
          for="groupSelector2"
          class="field-label"
        >
          <span v-if="orgType == 'edu'">
            CLASS
          </span>
          <span v-if="orgType != 'edu'">
            GROUP
          </span>
        </label>
        <select
          id="groupSelector2"
          v-model="selectedGroup"
          class="form-control"
        >
          <option
            v-for="group in speakerGroups"
            :value="group"
          >
            {{ group.name }}
          </option>
        </select>
      </div>
      <speaker-select
        v-if="showAlgorithmSelect"
        @algorithmChosen="algorithmChosen"
      />
      <assignment-sharing
        v-if="assignmentsEnabled && recordingType !== 'private'"
        ref-num="1"
        @sharingChanged="sharingChanged"
      />
      <div class="form-group">
        <label for="uploadFile">
          Audio file:
        </label><br>
        <label class="fileContainer btn btn-primary clear-btn">
          Choose File
          <!-- iOS bug treats accept="audio/*,video/*" as accept="photo/*,video/*" -->
          <input
            v-if="isIos"
            id="uploadFile"
            ref="fileInput"
            type="file"
            required
            @change="updateFilename"
          >
          <input
            v-else
            id="uploadFile"
            ref="fileInput"
            type="file"
            accept="audio/*,video/*"
            required
            @change="updateFilename"
          >
        </label> {{ filename }}
        <p class="help-block small">
          Many formats accepted. Max 200 MB, 15min
        </p>
      </div>
      <p v-show="!showAdvanced">
        <a
          href="#"
          @click.prevent="showAdvanced = true"
        >
          <small>Audio Clipping Controls</small>
        </a>
      </p>
      <div v-show="showAdvanced">
        <upload-trimming
          :trim="formValues.trim"
          count="1"
        />
      </div>
      <debug-recordings
        v-if="$store.getters.org && $store.getters.org.settings && $store.getters.org.settings.Debug_Recording_Options"
        @devFormData="loadDevFormData"
      />
      <loading-button
        type="submit"
        :loading="uploading"
        :progress="uploadProgress"
      >
        <span v-if="!uploading">
          Upload
        </span>
        <span v-if="uploading">
          <span v-if="uploadProgress < 1">
            Uploading
          </span>
          <span v-if="uploadProgress >= 1">
            Saving on server
          </span>
        </span>
      </loading-button>
    </form>
  </div>
</template>

<style lang="less" scoped>
  h2 {
    margin: 5px 0 15px 0;
  }
  #uploadTitle, #groupSelector2 {
    max-width: 300px;
  }
  .fileContainer {
    overflow: hidden;
    position: relative;
    margin-right: 12px;
  }

  .fileContainer [type=file] {
    cursor: inherit;
    display: block;
    font-size: 999px;
    filter: alpha(opacity=0);
    min-height: 100%;
    min-width: 100%;
    opacity: 0;
    position: absolute;
    right: 0;
    text-align: right;
    top: 0;
  }
</style>

<script>
import service from '../../services/recordings';
import UploadTrimming from '../forms/UploadTrimming';
import util from '../utils/util';
import AssignmentRecording from '../specials/AssignmentRecording';
import AssignmentSelect from '../specials/AssignmentSelect';
import SpeakerSelect from '../specials/SpeakerSelect';
import AssignmentSharing from '../specials/AssignmentSharing';
import DebugRecordings from '../specials/DebugRecordings';

export default {
  components: {AssignmentSelect, SpeakerSelect, UploadTrimming, AssignmentSharing, DebugRecordings},
  mixins: [AssignmentRecording],
  data() {
    return {
      uploadProgress: 0,
      isIos: util.isIos,
      uploading: false,
      showAdvanced: false,
      selectedGroup: null,
      filename: 'No file chosen',
      formValues: {
        title: '',
        source: 'ui upload form',
        submitted: false,
        feedbackRequested: false,
        trim: {
          from: 0,
          to: -1
        },
        algorithm: null,
      }
    };
  },
  computed: {
    speakerGroups() {
      let speakerGroups = this._.filter(this.$store.getters.groups, group => {
        return this._.includes(group.roles, 'speaker') && group.membershipStatus === 'active';
      });
      speakerGroups = this._.sortBy(
        speakerGroups, g => -new Date(g.createdDate).getTime()
      );

      // Set default selectedGroup
      if (!this.selectedGroup && speakerGroups.length) {
        this.selectedGroup = speakerGroups[0];
      }

      return speakerGroups;
    },
    orgType() {
      return this.$store.getters.org && this.$store.getters.org.type;
    },
    org() {
      return this.$store.getters.org;
    },
    videoEnabled() {
      return (this.org && typeof this.org.settings !== 'undefined' && this.org.settings.Video);
    },
  },
  created() {
    this.populateAssignmentAndTitle();
  },
  methods: {
    reset() {
      this.formValues.algorithm = null;
      this.formValues.title = '';
      this.formValues.isVideo = false;
      this.formValues.submitted = false;
      this.formValues.feedbackRequested = false;
      this.$refs.fileInput.value = '';
      this.formValues.trim = {
        from: 0,
        to: -1
      };
      this.showAdvanced = false;
    },
    updateFilename() {
      const fileExtension = util.getFileExtension(document.getElementById('uploadFile').value);
      if (fileExtension && this.videoEnabled && util.isIncompatibleVideo(fileExtension)) {
        alert(
          'Only video files in mp4 format can be displayed.  You will still receive an analysis ' +
          'for this recording, however please note a video playback screen will not be available ' +
          'for this file.'
        );
      }

      window.setTimeout( () => {
        const file = document.getElementById('uploadFile');
        this.filename = (file.value && file.value.replace(/([^\\]*\\)*/,'')) ?
          file.value.replace(/([^\\]*\\)*/,'') : 'No file chosen';
      }, 50);
    },
    upload() {
      const fullPath = document.getElementById('uploadFile').value;

      if (fullPath) {
        const fileExtension = util.getFileExtension(fullPath);

        // We only block uploads if we identified a 3 digit file suffix and it's not audio or video
        const acceptableExtensions = ['3gp', 'aac', 'aax', 'act', 'amr', 'awb', 'dct', 'dss', 'dvf',
          'gsm', 'ivs', 'm4a', 'm4b', 'm4p', 'mmf', 'mp3', 'mpc', 'msv', 'ogg', 'oga', 'raw', 'sln',
          'tta', 'vox', 'wav', 'wma', 'mkv', 'flv', 'vob', 'ogv', 'ogg', 'drc', 'avi', 'mov', 'wmv',
          'yuv', 'asf', 'amv', 'mp4', 'm4p', 'm4v', 'mpg', 'mp2', 'mpe', 'mpv', 'm2v', 'svi', '3gp',
          '3g2', 'nsv', 'flv', 'f4v', 'f4p', 'f4a', 'f4b' ];
        if (fileExtension && typeof fileExtension.length !== 'undefined' && fileExtension.length === 3 &&
            acceptableExtensions.indexOf(fileExtension) === -1) {
          return this.$store.dispatch('notify', {
            type: 'error',
            subject: 'Upload Error',
            text: 'You have submitted a file with an invalid filetype. Only audio and video files may be uploaded.',
          });
        }
      }

      const options = {
        progress: event => {
          this.uploadProgress = event.loaded / event.total;
        }
      };

      // Check for video
      if (this.videoEnabled) {
        const fileExtension = util.getFileExtension(document.getElementById('uploadFile').value);
        if (fileExtension && util.isMp4(fileExtension)) {
          this.formValues.isVideo = true;
        }
      }

      this.uploading = true;
      const formData = this.getFormData();

      service.upload(formData, options)
        .then(recording => {
          const recordingId = recording.recordingId;
          this.addRecordingToAssignment(recordingId);

          return this.$store.dispatch('listRecordings')
            .then(() => {
              this.reset();
              this.recordingUploaded(recordingId, formData);
            });
        })
        .catch(() => {
          this.$store.dispatch('notify', {
            type: 'error',
            subject: 'Upload Error',
            text: `Error uploading your file. Please retry your upload. If you attempted to upload a file
                larger than 200 MB, please use a smaller file.`
          });
        })
        .finally(() => {
          this.uploading = false;
          this.uploadProgress = 0;
        });
    }
  }
};
</script>
